import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Avatar,
  Chip,
  LinearProgress,
  Skeleton,
  CircularProgress,
  IconButton,
  Tooltip,
  TableCell,
  TableRow,
} from "@mui/material";
import {
  Event,
  People,
  AttachMoney,
  TrendingUp,
  TrendingDown,
  EmojiEvents,
  LocalOffer,
  CalendarMonth,
  Refresh,
  CheckCircle,
} from "@mui/icons-material";
import {
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  // ...existing code...
} from "recharts";
import useSessionValidator from "../../hooks/useSessionValidator";
import api from "../../services/api";
import { formatMonth, formatDay, parseFechaLocal } from "../../utils/dateUtils";
import EventEditModal from "../../components/admin/EventEditModal";

// ...existing code...
const AdminDashboard = () => {
  useSessionValidator();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [periodoFiltro, setPeriodoFiltro] = useState('mensual');
  const [loadingPeriodo, setLoadingPeriodo] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedEventoForEdit, setSelectedEventoForEdit] = useState(null);

  const PERIODOS = [
    { value: 'dias8',      label: '8 Días',     short: '8D'  },
    { value: 'dias15',     label: '15 Días',    short: '15D' },
    { value: 'mensual',    label: 'Mensual',    short: '1M'  },
    { value: 'trimestral', label: 'Trimestral', short: '3M'  },
    { value: 'semestral',  label: 'Semestral',  short: '6M'  },
    { value: 'anual',      label: 'Anual',      short: '1A'  },
  ];

  const handlePeriodoChange = async (nuevoPeriodo) => {
    if (nuevoPeriodo === periodoFiltro || loadingPeriodo) return;
    setPeriodoFiltro(nuevoPeriodo);
    setLoadingPeriodo(true);
    try {
      const response = await api.get(`/dashboard?periodo=${nuevoPeriodo}`);
      setData(response.data);
    } catch (err) {
      console.error('[Dashboard] Error al filtrar:', err);
    } finally {
      setLoadingPeriodo(false);
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/dashboard?periodo=${periodoFiltro}`);
      setData(response.data);
    } catch (err) {
      console.error("[Dashboard] Error:", err);
      setError("Error al cargar datos del dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Componente KPI Card — horizontal en móvil (2 cols), vertical en desktop
  // eslint-disable-next-line no-unused-vars
  const KPICard = ({
    title,
    value,
    icon,
    color,
    trend,
    trendLabel,
    prefix = "",
    suffix = "",
  }) => (
    <Card
      sx={{
        background:
          "linear-gradient(145deg, rgba(19, 24, 41, 0.95) 0%, rgba(30, 36, 60, 0.95) 100%)",
        border: "1px solid rgba(255, 255, 255, 0.08)",
        borderRadius: { xs: 2.5, sm: 3 },
        position: "relative",
        overflow: "hidden",
        transition: "transform 0.2s, box-shadow 0.2s",
        height: "100%",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: `0 12px 30px ${color}40`,
          border: `1px solid ${color}40`,
        },
      }}
    >
      <CardContent
        sx={{
          p: { xs: "14px !important", sm: "20px !important" },
          display: "flex",
          flexDirection: { xs: "row", sm: "column" },
          alignItems: "center",
          justifyContent: { xs: "flex-start", sm: "center" },
          textAlign: { xs: "left", sm: "center" },
          gap: { xs: 1.5, sm: 0 },
          minHeight: { xs: 80, sm: 130 },
          height: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* Icono */}
        <Box
          sx={{
            p: { xs: 1, sm: 1.5 },
            borderRadius: 2,
            background: `linear-gradient(135deg, ${color}30 0%, ${color}10 100%)`,
            border: `1px solid ${color}40`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {React.cloneElement(icon, {
            sx: { color: color, fontSize: { xs: 22, sm: 28 } },
          })}
        </Box>
        {/* Texto */}
        <Box sx={{ flex: 1, minWidth: 0, mt: { xs: 0, sm: 1 } }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontWeight: 500,
              fontSize: { xs: "0.68rem", sm: "0.85rem" },
              lineHeight: 1.3,
              mb: { xs: 0.25, sm: 0.5 },
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: color,
              fontSize: { xs: "1.35rem", sm: "2rem" },
              letterSpacing: "-0.5px",
              lineHeight: 1,
            }}
          >
            {prefix}
            {typeof value === "number" ? value.toLocaleString() : value}
            {suffix}
          </Typography>
          {trend !== undefined && trend !== null && (
            <Chip
              size="small"
              icon={
                trend >= 0 ? (
                  <TrendingUp sx={{ fontSize: 10 }} />
                ) : (
                  <TrendingDown sx={{ fontSize: 10 }} />
                )
              }
              label={`${trend >= 0 ? "+" : ""}${trend}%`}
              sx={{
                mt: 0.5,
                bgcolor:
                  trend >= 0
                    ? "rgba(76, 175, 80, 0.15)"
                    : "rgba(244, 67, 54, 0.15)",
                color: trend >= 0 ? "#4CAF50" : "#F44336",
                fontWeight: 600,
                fontSize: "0.6rem",
                height: 18,
                display: { xs: "none", sm: "flex" },
                "& .MuiChip-icon": { fontSize: 10 },
              }}
            />
          )}
          {trendLabel && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                mt: 0.25,
                fontSize: "0.7rem",
                display: { xs: "none", sm: "block" },
              }}
            >
              {trendLabel}
            </Typography>
          )}
        </Box>
      </CardContent>
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: { xs: 3, sm: 4 },
          background: `linear-gradient(90deg, ${color} 0%, ${color}60 100%)`,
        }}
      />
    </Card>
  );

  // Componente KPI circular — circular en TODOS los tamaños
  const CircularKPICard = ({ title, value, icon, color, prefix = "", suffix = "" }) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: { xs: 100, sm: 152, md: 168 },
        height: { xs: 100, sm: 152, md: 168 },
        borderRadius: "50%",
        background: "linear-gradient(145deg, rgba(19, 24, 41, 0.95) 0%, rgba(30, 36, 60, 0.95) 100%)",
        border: `2px solid ${color}40`,
        boxShadow: `0 4px 20px ${color}30`,
        transition: "transform 0.2s, box-shadow 0.2s",
        flexShrink: 0,
        "&:hover": {
          transform: "scale(1.06)",
          boxShadow: `0 8px 30px ${color}50`,
        },
      }}
    >
      <Avatar
        sx={{
          bgcolor: color + "30",
          color,
          width: { xs: 26, sm: 42 },
          height: { xs: 26, sm: 42 },
          mb: { xs: 0.25, sm: 0.75 },
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: { xs: 14, sm: 22 } } })}
      </Avatar>
      <Typography
        sx={{
          color,
          fontWeight: 700,
          fontSize: { xs: "1rem", sm: "1.85rem", md: "2rem" },
          lineHeight: 1,
          mb: 0.2,
        }}
      >
        {prefix}{typeof value === "number" ? value.toLocaleString() : value}{suffix}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          fontSize: { xs: "0.52rem", sm: "0.7rem" },
          fontWeight: 500,
          lineHeight: 1.2,
          textAlign: "center",
          px: { xs: 0.5, sm: 1 },
          display: "block",
        }}
      >
        {title}
      </Typography>
    </Box>
  );

  // Componente Top Vendedor Card
  // ── Pódium móvil (orden: 2°, 1°, 3°) ──────────────────────────────────────
  const PodiumDisplay = ({ vendedores }) => {
    const medalColors = ["#FFD700", "#C0C0C0", "#CD7F32"];
    const podiumGlow = [
      "0 0 24px rgba(255,215,0,0.55), 0 0 8px rgba(255,215,0,0.3)",
      "0 0 16px rgba(192,192,192,0.4), 0 0 6px rgba(192,192,192,0.2)",
      "0 0 14px rgba(205,127,50,0.4), 0 0 5px rgba(205,127,50,0.2)",
    ];
    const podiumBg = [
      "linear-gradient(180deg, rgba(255,215,0,0.18) 0%, rgba(255,215,0,0.06) 100%)",
      "linear-gradient(180deg, rgba(192,192,192,0.15) 0%, rgba(192,192,192,0.05) 100%)",
      "linear-gradient(180deg, rgba(205,127,50,0.14) 0%, rgba(205,127,50,0.05) 100%)",
    ];
    // display order: índice 1 (2°), índice 0 (1°), índice 2 (3°)
    const displayOrder = [1, 0, 2];
    const podiumHeights = [72, 104, 52];
    const avatarSizes = [54, 70, 48];

    return (
      <Box sx={{ pt: 2, pb: 0.5, px: 3 }}>
        {/* Título corona */}
        <Box sx={{ textAlign: "center", mb: 1 }}>
          <Typography sx={{ fontSize: "1.4rem", lineHeight: 1 }}>👑</Typography>
        </Box>
        {/* Columnas del pódium */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-end",
            gap: 1,
          }}
        >
          {displayOrder.map((rankIdx, colIdx) => {
            const v = vendedores[rankIdx];
            const color = medalColors[rankIdx];
            const avatarSize = avatarSizes[colIdx];
            const blockH = podiumHeights[colIdx];
            const isFirst = rankIdx === 0;
            const medals = ["🥇", "🥈", "🥉"];

            // ── Slot vacío ──────────────────────────────────────────────────
            if (!v) {
              return (
                <Box
                  key={`empty-${rankIdx}`}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    flex: isFirst ? "0 0 36%" : "0 0 28%",
                  }}
                >
                  <Box sx={{ position: "relative", mb: 0.75 }}>
                    <Avatar
                      sx={{
                        width: avatarSize,
                        height: avatarSize,
                        bgcolor: `${color}0d`,
                        border: `3px dashed ${color}35`,
                        color: `${color}50`,
                        fontSize: isFirst ? "1.5rem" : "1.2rem",
                      }}
                    >
                      ?
                    </Avatar>
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: -5,
                        right: -5,
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        bgcolor: `${color}20`,
                        border: `2px dashed ${color}40`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography sx={{ fontSize: 10, fontWeight: 900, color: `${color}60`, lineHeight: 1 }}>
                        {rankIdx + 1}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography noWrap sx={{ fontWeight: 500, fontSize: isFirst ? "0.8rem" : "0.7rem", color: `${color}40`, mb: 0.25 }}>
                    Sin datos
                  </Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: isFirst ? "1.1rem" : "0.9rem", color: `${color}30`, lineHeight: 1 }}>
                    —
                  </Typography>
                  <Typography sx={{ fontSize: "0.6rem", color: "text.secondary", mb: 0.75, textTransform: "uppercase" }}>
                    eventos
                  </Typography>
                  <Box
                    sx={{
                      width: "100%",
                      height: `${blockH}px`,
                      background: `linear-gradient(180deg, ${color}08 0%, transparent 100%)`,
                      border: `1px dashed ${color}20`,
                      borderBottom: "none",
                      borderRadius: "10px 10px 0 0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: isFirst ? "1.6rem" : "1.2rem", lineHeight: 1, opacity: 0.2 }}>
                      {medals[rankIdx]}
                    </Typography>
                  </Box>
                </Box>
              );
            }

            // ── Slot con datos ───────────────────────────────────────────────

            return (
              <Box
                key={v.id}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  flex: isFirst ? "0 0 36%" : "0 0 28%",
                }}
              >
                {/* Avatar */}
                <Box sx={{ position: "relative", mb: 0.75 }}>
                  <Avatar
                    src={v.foto_url}
                    sx={{
                      width: avatarSize,
                      height: avatarSize,
                      bgcolor: color,
                      border: `3px solid ${color}`,
                      fontWeight: 700,
                      fontSize: isFirst ? "1.4rem" : "1.1rem",
                      boxShadow: podiumGlow[rankIdx],
                    }}
                  >
                    {v.nombre_completo?.charAt(0) || "V"}
                  </Avatar>
                  {/* Badge posición */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: -5,
                      right: -5,
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      bgcolor: color,
                      border: "2px solid #0d1120",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: podiumGlow[rankIdx],
                    }}
                  >
                    <Typography sx={{ fontSize: 10, fontWeight: 900, color: "#000", lineHeight: 1 }}>
                      {rankIdx + 1}
                    </Typography>
                  </Box>
                </Box>

                {/* Nombre */}
                <Typography
                  noWrap
                  sx={{
                    fontWeight: 700,
                    fontSize: isFirst ? "0.82rem" : "0.72rem",
                    color: color,
                    maxWidth: "100%",
                    textAlign: "center",
                    px: 0.5,
                    mb: 0.25,
                    textShadow: isFirst ? `0 0 12px ${color}80` : "none",
                  }}
                >
                  {v.nombre_completo?.split(" ")[0]}
                </Typography>

                {/* Eventos */}
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: isFirst ? "1.15rem" : "0.95rem",
                    color: color,
                    lineHeight: 1,
                  }}
                >
                  {v.total_eventos}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "text.secondary",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  eventos
                </Typography>

                {/* Bloque del pódium */}
                <Box
                  sx={{
                    width: "100%",
                    height: `${blockH}px`,
                    background: podiumBg[rankIdx],
                    border: `1px solid ${color}50`,
                    borderBottom: "none",
                    borderRadius: "10px 10px 0 0",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.25,
                    boxShadow: `inset 0 2px 8px ${color}20`,
                  }}
                >
                  <Typography sx={{ fontSize: isFirst ? "1.6rem" : "1.2rem", lineHeight: 1 }}>
                    {medals[rankIdx]}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.6rem",
                      color: color,
                      fontWeight: 700,
                      opacity: 0.8,
                      textTransform: "uppercase",
                    }}
                  >
                    {v.especialidad?.split(" ")[0] || "—"}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
        {/* Base del pódium */}
        <Box
          sx={{
            height: 6,
            mx: 0,
            background: "linear-gradient(90deg, rgba(205,127,50,0.4) 0%, rgba(192,192,192,0.5) 28%, rgba(255,215,0,0.7) 50%, rgba(192,192,192,0.5) 72%, rgba(205,127,50,0.4) 100%)",
            borderRadius: "0 0 4px 4px",
            boxShadow: "0 2px 12px rgba(255,215,0,0.3)",
          }}
        />
      </Box>
    );
  };

  const TopVendedorCard = ({ vendedor, rank }) => {
    const medalColors = ["#FFD700", "#C0C0C0", "#CD7F32"];

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
          bgcolor:
            rank === 0
              ? "rgba(255, 215, 0, 0.08)"
              : "rgba(255, 255, 255, 0.02)",
          borderRadius: 2,
          border: "1px solid",
          borderColor:
            rank === 0 ? "rgba(255, 215, 0, 0.2)" : "rgba(255, 255, 255, 0.05)",
          transition: "all 0.2s",
          "&:hover": { bgcolor: "rgba(255, 255, 255, 0.05)" },
        }}
      >
        <Box sx={{ position: "relative" }}>
          <Avatar
            src={vendedor.foto_url}
            sx={{
              width: 48,
              height: 48,
              bgcolor: medalColors[rank] || "#666",
              border: `2px solid ${medalColors[rank] || "#666"}`,
              fontWeight: 700,
            }}
          >
            {vendedor.nombre_completo?.charAt(0) || "V"}
          </Avatar>
          <Box
            sx={{
              position: "absolute",
              bottom: -4,
              right: -4,
              bgcolor: medalColors[rank] || "#666",
              borderRadius: "50%",
              width: 20,
              height: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#000" }}>
              {rank + 1}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
            {vendedor.nombre_completo}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {vendedor.especialidad || "Colaborador"}
          </Typography>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Typography sx={{ fontWeight: 700, color: "#00C9FF" }}>
            {vendedor.total_eventos}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            eventos
          </Typography>
        </Box>
      </Box>
    );
  };

  // Componente Top Paquete/Servicio
  const TopPaqueteItem = ({ paquete, rank, maxSolicitudes }) => (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 0.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.85rem",
              color: rank === 0 ? "#FFD700" : "text.primary",
            }}
          >
            #{rank + 1}
          </Typography>
          <Typography sx={{ fontWeight: 500, fontSize: "0.9rem" }} noWrap>
            {paquete.nombre}
          </Typography>
        </Box>
        <Chip
          label={`${paquete.total_solicitudes} eventos`}
          size="small"
          sx={{
            ml: 2,
            flexShrink: 0,
            bgcolor: "rgba(0, 201, 255, 0.15)",
            color: "#00C9FF",
            fontWeight: 600,
            fontSize: "0.7rem",
          }}
        />
      </Box>
      <LinearProgress
        variant="determinate"
        value={(paquete.total_solicitudes / maxSolicitudes) * 100}
        sx={{
          height: 6,
          borderRadius: 3,
          bgcolor: "rgba(255, 255, 255, 0.08)",
          "& .MuiLinearProgress-bar": {
            borderRadius: 3,
            background:
              rank === 0
                ? "linear-gradient(90deg, #FFD700 0%, #FFA000 100%)"
                : "linear-gradient(90deg, #00C9FF 0%, #0077B6 100%)",
          },
        }}
      />
    </Box>
  );

  // Componente Próximo Evento
  // eslint-disable-next-line no-unused-vars
  const ProximoEventoRow = ({ evento }) => {
    const fechaEvento = parseFechaLocal(evento.fecha_evento);
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const diffDays = fechaEvento
      ? Math.ceil((fechaEvento - hoy) / (1000 * 60 * 60 * 24))
      : 0;

    const getEstadoColor = (estado) => {
      const colors = {
        Confirmado: "#00C9FF",
        "Por Confirmar": "#FFC107",
        Pendiente: "#FF9800",
      };
      return colors[estado] || "#666";
    };

    return (
      <TableRow sx={{ "&:hover": { bgcolor: "rgba(255, 255, 255, 0.03)" } }}>
        <TableCell sx={{ py: 1.5, borderColor: "rgba(255, 255, 255, 0.05)" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor:
                  diffDays === 0
                    ? "rgba(244, 67, 54, 0.15)"
                    : "rgba(0, 201, 255, 0.1)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: diffDays === 0 ? "#F44336" : "#00C9FF",
                }}
              >
                {formatDay(evento.fecha_evento)}
              </Typography>
              <Typography
                sx={{
                  fontSize: 8,
                  color: "text.secondary",
                  textTransform: "uppercase",
                }}
              >
                {formatMonth(evento.fecha_evento)}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }} noWrap>
                {evento.titulo}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {evento.cliente_nombre || "Sin cliente"}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell sx={{ py: 1.5, borderColor: "rgba(255, 255, 255, 0.05)" }}>
          <Typography variant="body2">
            {evento.hora_inicio || "--:--"}
          </Typography>
        </TableCell>
        <TableCell sx={{ py: 1.5, borderColor: "rgba(255, 255, 255, 0.05)" }}>
          <Chip
            label={evento.estado}
            size="small"
            sx={{
              bgcolor: `${getEstadoColor(evento.estado)}20`,
              color: getEstadoColor(evento.estado),
              fontWeight: 500,
              fontSize: "0.7rem",
            }}
          />
        </TableCell>
        <TableCell
          align="right"
          sx={{ py: 1.5, borderColor: "rgba(255, 255, 255, 0.05)" }}
        >
          <Typography sx={{ fontWeight: 600, color: "#92FE9D" }}>
            ${(evento.costo_servicio || 0).toLocaleString()}
          </Typography>
        </TableCell>
      </TableRow>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Skeleton variant="text" width={300} height={50} sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton
                variant="rounded"
                height={140}
                sx={{ borderRadius: 3 }}
              />
            </Grid>
          ))}
          <Grid item xs={12} md={8}>
            <Skeleton variant="rounded" height={350} sx={{ borderRadius: 3 }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rounded" height={350} sx={{ borderRadius: 3 }} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  const stats = data?.stats || {};
  const ingresosPorMes = data?.ingresosPorMes || [];
  const topVendedores = data?.topVendedores || [];
  const topPaquetes = data?.topPaquetes || [];
  const proximosEventos = data?.proximosEventos || [];

  const handleOpenEditModal = (evento) => {
    setSelectedEventoForEdit(evento);
    setEditModalOpen(true);
  };

  const handleEditModalSave = () => {
    fetchDashboardData();
  };

  const maxSolicitudes =
    topPaquetes.length > 0
      ? Math.max(...topPaquetes.map((p) => p.total_solicitudes))
      : 1;

  return (
    <>
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        minHeight: "100vh",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: { xs: 2, sm: 4 },
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 0.5,
              fontSize: { xs: "1.25rem", sm: "2.125rem" },
            }}
          >
            Dashboard Administrativo
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ display: { xs: "none", sm: "block" } }}
          >
            Resumen de métricas y rendimiento del negocio
          </Typography>
        </Box>
        <Tooltip title="Actualizar datos">
          <IconButton
            onClick={fetchDashboardData}
            sx={{ bgcolor: "rgba(255, 255, 255, 0.05)" }}
          >
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Paper
          sx={{
            p: 2,
            mb: 3,
            bgcolor: "rgba(244, 67, 54, 0.1)",
            border: "1px solid rgba(244, 67, 54, 0.3)",
          }}
        >
          <Typography color="error">{error}</Typography>
        </Paper>
      )}

      {/* ── Fila 1: Tarjetas circulares de eventos ── */}
      <Grid
        container
        spacing={{ xs: 1, sm: 4 }}
        justifyContent="center"
        sx={{ mb: { xs: 2, sm: 3 } }}
      >
        {/* Total Eventos */}
        <Grid item xs={4} sm="auto" sx={{ display: "flex", justifyContent: "center" }}>
          <CircularKPICard
            title="Total Eventos"
            value={stats.totalEventos || 0}
            icon={<Event />}
            color="#00C9FF"
          />
        </Grid>
        {/* Pendientes */}
        <Grid item xs={4} sm="auto" sx={{ display: "flex", justifyContent: "center" }}>
          <CircularKPICard
            title="Pendientes"
            value={stats.eventosPendientes || 0}
            icon={<CalendarMonth />}
            color="#FF5252"
          />
        </Grid>
        {/* Completados — en la misma fila que los anteriores */}
        <Grid item xs={4} sm="auto" sx={{ display: "flex", justifyContent: "center" }}>
          <CircularKPICard
            title="Completados"
            value={stats.eventosCompletadosMes || 0}
            icon={<CheckCircle />}
            color="#4CAF50"
          />
        </Grid>
      </Grid>

      {/* ── Selector de período ── */}
      <Box
        sx={{
          display: "flex",
          gap: 0.5,
          mb: { xs: 2, sm: 3 },
          p: 0.75,
          bgcolor: "rgba(255,255,255,0.04)",
          borderRadius: 3,
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        {PERIODOS.map((p) => (
          <Box
            key={p.value}
            onClick={() => handlePeriodoChange(p.value)}
            sx={{
              flex: 1,
              textAlign: "center",
              py: { xs: 0.875, sm: 1 },
              borderRadius: 2,
              cursor: loadingPeriodo ? "wait" : "pointer",
              background: periodoFiltro === p.value
                ? "linear-gradient(135deg, #00C9FF 0%, #0077B6 100%)"
                : "transparent",
              color: periodoFiltro === p.value ? "#000" : "text.secondary",
              fontWeight: periodoFiltro === p.value ? 700 : 500,
              transition: "all 0.25s ease",
              userSelect: "none",
              "&:hover": {
                bgcolor: periodoFiltro !== p.value ? "rgba(255,255,255,0.06)" : undefined,
              },
            }}
          >
            <Typography
              sx={{ fontWeight: "inherit", fontSize: { xs: "0.72rem", sm: "0.85rem" }, color: "inherit", lineHeight: 1 }}
            >
              <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>{p.label}</Box>
              <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>{p.short}</Box>
            </Typography>
          </Box>
        ))}
      </Box>

      {/* ── Secciones dinámicas ── */}
      <Box sx={{ position: "relative" }}>
        {loadingPeriodo && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              pt: 8,
              bgcolor: "rgba(13,17,32,0.55)",
              backdropFilter: "blur(3px)",
              borderRadius: 2,
            }}
          >
            <CircularProgress sx={{ color: "#00C9FF" }} size={40} />
          </Box>
        )}
        <Box sx={{ opacity: loadingPeriodo ? 0.4 : 1, transition: "opacity 0.3s" }}>

      {/* ── Próximos Eventos — visible en todos los tamaños ── */}
      <Paper
        sx={{
          p: { xs: 2, sm: 3 },
          mb: { xs: 2, sm: 3 },
          background: "linear-gradient(145deg, rgba(19, 24, 41, 0.95) 0%, rgba(30, 36, 60, 0.95) 100%)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          borderRadius: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <CalendarMonth sx={{ color: "#FF5252", fontSize: 22 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Próximos {{ dias8: '8 días', dias15: '15 días', mensual: '30 días', trimestral: '3 meses', semestral: '6 meses', anual: '12 meses' }[periodoFiltro]}
          </Typography>
        </Box>
        {proximosEventos.length > 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {proximosEventos.slice(0, 5).map((evento) => {
              const fechaEvento = parseFechaLocal(evento.fecha_evento);
              const hoy = new Date();
              hoy.setHours(0, 0, 0, 0);
              const diffDays = fechaEvento
                ? Math.ceil((fechaEvento - hoy) / (1000 * 60 * 60 * 24))
                : 0;
              return (
                <Box
                  key={evento.id}
                  onClick={() => handleOpenEditModal(evento)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.5,
                    bgcolor: "rgba(255,255,255,0.03)",
                    borderRadius: 2,
                    border: "1px solid rgba(255,255,255,0.05)",
                    cursor: "pointer",
                    transition: "all 0.18s ease",
                    "&:hover": {
                      bgcolor: "rgba(0, 201, 255, 0.07)",
                      border: "1px solid rgba(0, 201, 255, 0.25)",
                      transform: "translateX(3px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: 1.5,
                      bgcolor: diffDays === 0 ? "rgba(244, 67, 54, 0.15)" : "rgba(0, 201, 255, 0.1)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: diffDays === 0 ? "#F44336" : "#00C9FF", lineHeight: 1 }}>
                      {formatDay(evento.fecha_evento)}
                    </Typography>
                    <Typography sx={{ fontSize: 9, color: "text.secondary", textTransform: "uppercase" }}>
                      {formatMonth(evento.fecha_evento)}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }} noWrap>
                      {evento.titulo}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {evento.hora_inicio || "--:--"} · {evento.cliente_nombre || "Sin cliente"}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 700, color: "#92FE9D", fontSize: "0.9rem", flexShrink: 0 }}>
                    ${(evento.costo_servicio || 0).toLocaleString()}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Typography color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
            No hay eventos en este período
          </Typography>
        )}
      </Paper>


      {/* ── Fila 2: Tarjetas circulares de métricas ── */}
      <Grid
        container
        spacing={{ xs: 1, sm: 4 }}
        justifyContent="center"
        sx={{ mb: { xs: 2, sm: 4 } }}
      >
        {/* Total Clientes */}
        <Grid item xs={4} sm="auto" sx={{ display: "flex", justifyContent: "center" }}>
          <CircularKPICard
            title="Total Clientes"
            value={stats.totalClientes || 0}
            icon={<People />}
            color="#92FE9D"
          />
        </Grid>
        {/* Ingresos del Mes */}
        <Grid item xs={4} sm="auto" sx={{ display: "flex", justifyContent: "center" }}>
          <CircularKPICard
            title="Ingresos del Mes"
            value={stats.ingresosMes || 0}
            icon={<AttachMoney />}
            color="#FFC107"
            prefix="$"
          />
        </Grid>
        {/* Tasa Conversión */}
        <Grid item xs={4} sm="auto" sx={{ display: "flex", justifyContent: "center" }}>
          <CircularKPICard
            title="Tasa Conversión"
            value={stats.tasaConversion || 0}
            icon={<TrendingUp />}
            color="#9C27B0"
            suffix="%"
          />
        </Grid>
      </Grid>
      {/* Panels Grid: Top Vendedores · Top Servicios */}
      <Grid container columnSpacing={{ xs: 0, sm: 3 }} rowSpacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 2, sm: 3 } }}>
      {/* Top Vendedores */}
      <Grid item xs={12} md={6} sx={{ mb: { xs: 0, sm: 0 } }}>
        <Paper
          sx={{
            p: { xs: 2, sm: 3 },
            background:
              "linear-gradient(145deg, rgba(19, 24, 41, 0.95) 0%, rgba(30, 36, 60, 0.95) 100%)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            borderRadius: 3,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: { xs: 1, sm: 3 }, flexShrink: 0 }}>
            <EmojiEvents sx={{ color: "#FFD700" }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Top 3 Colaboradores
            </Typography>
          </Box>
          <Box sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            pr: 0.5,
            "&::-webkit-scrollbar": { width: 3 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": { background: "rgba(255,255,255,0.2)", borderRadius: 2 },
          }}>
          {topVendedores.length > 0 ? (
            <>
              {/* Pódium — solo móvil */}
              <Box sx={{ display: { xs: "block", sm: "none" } }}>
                <PodiumDisplay vendedores={topVendedores} />
              </Box>
              {/* Lista — desktop */}
              <Box sx={{ display: { xs: "none", sm: "flex" }, flexDirection: "column", gap: 1.5 }}>
                {topVendedores.map((vendedor, idx) => (
                  <TopVendedorCard
                    key={vendedor.id}
                    vendedor={vendedor}
                    rank={idx}
                  />
                ))}
              </Box>
            </>
          ) : (
            <Typography
              color="text.secondary"
              sx={{ textAlign: "center", py: 3 }}
            >
              Sin datos suficientes
            </Typography>
          )}
          </Box>
        </Paper>
      </Grid>

      {/* Top Paquetes/Servicios */}
      <Grid item xs={12} md={6}>
        <Paper
          sx={{
            p: { xs: 2, sm: 3 },
            background:
              "linear-gradient(145deg, rgba(19, 24, 41, 0.95) 0%, rgba(30, 36, 60, 0.95) 100%)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            borderRadius: 3,
            height: "100%",
            maxHeight: { xs: 340, sm: "none" },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: { xs: 2, sm: 3 }, flexShrink: 0 }}>
            <LocalOffer sx={{ color: "#00C9FF" }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Top 5 Servicios
            </Typography>
          </Box>
          <Box sx={{
            flex: 1,
            overflow: "auto",
            pr: 0.5,
            "&::-webkit-scrollbar": { width: 3 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": { background: "rgba(255,255,255,0.2)", borderRadius: 2 },
          }}>
          {topPaquetes.length > 0 ? (
            <Box>
              {topPaquetes.map((paquete, idx) => (
                <TopPaqueteItem
                  key={paquete.id}
                  paquete={paquete}
                  rank={idx}
                  maxSolicitudes={maxSolicitudes}
                />
              ))}
            </Box>
          ) : (
            <Typography
              color="text.secondary"
              sx={{ textAlign: "center", py: 3 }}
            >
              Sin datos suficientes
            </Typography>
          )}
          </Box>
        </Paper>
      </Grid>

      </Grid>
      {/* Fin del Grid de paneles */}

      {/* Ingresos Chart */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Paper
            sx={{
              p: { xs: 2, sm: 3 },
              background:
                "linear-gradient(145deg, rgba(19, 24, 41, 0.95) 0%, rgba(30, 36, 60, 0.95) 100%)",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              borderRadius: 3,
            }}
          >
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Tendencia de Ingresos · {{ dias8: 'últimos 8 días', dias15: 'últimos 15 días', mensual: 'último mes', trimestral: 'últimos 3 meses', semestral: 'últimos 6 meses', anual: 'último año' }[periodoFiltro]}
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={ingresosPorMes}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.08)"
                />
                <XAxis dataKey="mes" stroke="#B0BEC5" />
                <YAxis
                  stroke="#92FE9D"
                  width={60}
                  tickFormatter={(value) =>
                    value >= 1000
                      ? `$${(value / 1000).toFixed(0)}k`
                      : `$${value}`
                  }
                />
                <RechartsTooltip
                  contentStyle={{
                    background: "#1a1f35",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 8,
                  }}
                  formatter={(value) => [
                    `$${value.toLocaleString()}`,
                    "Ingresos",
                  ]}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="ingresos"
                  stroke="#92FE9D"
                  strokeWidth={3}
                  dot={{ fill: "#92FE9D", strokeWidth: 2, r: 5 }}
                  activeDot={{
                    r: 7,
                    fill: "#fff",
                    stroke: "#92FE9D",
                    strokeWidth: 3,
                  }}
                  name="Ingresos"
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
        </Box>
      </Box>
    </Box>

    {/* ── Modal edición / detalle de evento ── */}
    <EventEditModal
      open={editModalOpen}
      onClose={() => {
        setEditModalOpen(false);
        setSelectedEventoForEdit(null);
      }}
      evento={selectedEventoForEdit}
      colaboradores={[]}
      onSave={handleEditModalSave}
    />
    </>
  );
};

export default AdminDashboard;
